import * as _ from 'underscore'

import { LocalizationHelper } from '../../helpers'

import { Enums } from '../../models/common'
import { OpOperator, OpOperatorTechnicalPermission, OpTechnicalPermission } from '../../models/core'

import { ColumnModel } from '../../modules/api/api.models'

import { TECHNICAL_PERMISSION_SERVICE_NAME, TechnicalPermissionService } from '../technical-permission'
import { OPERATOR_SERVICE_NAME, OperatorService } from './operator.service'
import { REFERENCE_TYPE_SERVICE_NAME, ReferenceTypeService, } from '../reference-type'


//#region Consts

export const OPERATOR_TECHNICAL_PERMISSIONS_SERVICE_NAME: string = 'OperatorTechnicalPermissionsService'
export const OPERATOR_TECHNICAL_PERMISSIONS_SERVICE_PROVIDER_NAME: string = 'OperatorTechnicalPermissionsServiceProvider'

//#endregion

export class OperatorTechnicalPermissionsService {

    //#region Consts

    public static get LOG_PREFIX(): string { return '[SIMAX.Services.Operator.TechnicalPermissions]' }

    //#endregion


    //#region Ctor

    constructor(
        private operatorService: OperatorService,
        private referenceTypeService: ReferenceTypeService,
        private technicalPermissionService: TechnicalPermissionService
    ) { }

    //#endregion


    //#region Columns

    public getOperatorTechnicalPermissionsGridColumns(): ColumnModel[] {
        const [nameColumn, codeColumn] = this.technicalPermissionService.getTechnicalPermissionsGridColumns()
        const startTimeColumn = new ColumnModel()
        const endTimeColumn = new ColumnModel()
        const notesMasterDetailsColumn = new ColumnModel()

        nameColumn.idName = 'technicalPermission'
        codeColumn.idName = 'technicalPermission.code'

        startTimeColumn.idName = 'startTime'
        startTimeColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.StartTime)
        startTimeColumn.type = Enums.DataTypeClass.Datetime

        endTimeColumn.idName = 'endTime'
        endTimeColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.EndTime)
        endTimeColumn.type = Enums.DataTypeClass.Datetime

        notesMasterDetailsColumn.idName = 'notes'
        notesMasterDetailsColumn.type = Enums.DataTypeClass.Text
        notesMasterDetailsColumn.hidden = true

        return [nameColumn, codeColumn, startTimeColumn, endTimeColumn, notesMasterDetailsColumn]
    }

    public getAvailableGridEditColumns(): ColumnModel[] {
        return this.technicalPermissionService.getTechnicalPermissionsGridColumns()
    }

    //#endregion


    //#region Save

    public saveTechnicalPermissionsToOperator(
        operator: OpOperator,
        addedOperatorTechnicalPermissions: OpOperatorTechnicalPermission[],
        removedOperatorTechnicalPermissions: OpOperatorTechnicalPermission[],
        modifiedOperatorTechnicalPermissions: OpOperatorTechnicalPermission[]
    ): JQueryPromise<OpOperator> {
        const operatorToSave: OpOperator = new OpOperator(null)

        operatorToSave.idOperator = operator.idOperator
        operatorToSave.actor = operator.actor.clone()
        operatorToSave.actor.avatar = null
        operatorToSave.opState = Enums.OpChangeState.Modified
        operatorToSave.operatorTechnicalPermissions = []

        _.forEach(addedOperatorTechnicalPermissions, (addedTechnicalPermission: OpOperatorTechnicalPermission) => {
            const clonedTechnicalPermission: OpOperatorTechnicalPermission = addedTechnicalPermission.clone()

            clonedTechnicalPermission.opState = Enums.OpChangeState.New

            operatorToSave.operatorTechnicalPermissions.push(clonedTechnicalPermission)
        })

        _.forEach(removedOperatorTechnicalPermissions, (removedTechnicalPermission: OpOperatorTechnicalPermission) => {
            const clonedTechnicalPermission: OpOperatorTechnicalPermission = removedTechnicalPermission.clone()

            clonedTechnicalPermission.opState = Enums.OpChangeState.Delete

            operatorToSave.operatorTechnicalPermissions.push(clonedTechnicalPermission)
        })

        _.forEach(modifiedOperatorTechnicalPermissions, (modifiedTechnicalPermission: OpOperatorTechnicalPermission) => {
            const clonedTechnicalPermission: OpOperatorTechnicalPermission = modifiedTechnicalPermission.clone()

            clonedTechnicalPermission.opState = Enums.OpChangeState.Modified

            operatorToSave.operatorTechnicalPermissions.push(clonedTechnicalPermission)
        })

        const removedOperatorIds: number[] = removedOperatorTechnicalPermissions.map((removedTechnicalPermission: OpOperatorTechnicalPermission) => removedTechnicalPermission.idOperatorTechnicalPermission)
        const modifiedOperatorIds: number[] = modifiedOperatorTechnicalPermissions.map((modifiedTechnicalPermission: OpOperatorTechnicalPermission) => modifiedTechnicalPermission.idOperatorTechnicalPermission)

        operator.operatorTechnicalPermissions
            .filter((loadedTechnicalPermission: OpOperatorTechnicalPermission) => !_.include([...modifiedOperatorIds, ...removedOperatorIds], loadedTechnicalPermission.idOperatorTechnicalPermission))
            .forEach((loadedTechnicalPermission: OpOperatorTechnicalPermission) => {
                const clonedTechnicalPermission: OpOperatorTechnicalPermission = loadedTechnicalPermission.clone()

                clonedTechnicalPermission.opState = Enums.OpChangeState.Loaded

                operatorToSave.operatorTechnicalPermissions.push(clonedTechnicalPermission)
            })

        return this.operatorService.saveOperator(operatorToSave)
    }

    public saveTechnicalPermissionsGridEditResult(operator: OpOperator, added: any[], removed: any[], grid: any) {
        const addedOperatorTechnicalPermissions: OpOperatorTechnicalPermission[] = []
        const modifiedOperatorTechnicalPermissions: OpOperatorTechnicalPermission[] = []
        const removedOperatorTechnicalPermissions: OpOperatorTechnicalPermission[] = []

        _.forEach(grid, (gridItem: any) => {
            if (gridItem.isNew) {
                const operatorTechnicalPermissionToAdd = new OpOperatorTechnicalPermission(null)

                operatorTechnicalPermissionToAdd.endTime = gridItem.endTime
                operatorTechnicalPermissionToAdd.idOperator = operator.idOperator
                operatorTechnicalPermissionToAdd.idTechnicalPermission = gridItem.id
                operatorTechnicalPermissionToAdd.notes = gridItem.notes
                operatorTechnicalPermissionToAdd.opState = Enums.OpChangeState.New
                operatorTechnicalPermissionToAdd.startTime = gridItem.startTime

                addedOperatorTechnicalPermissions.push(operatorTechnicalPermissionToAdd)
            }
        })

        _.forEach(grid, (gridItem: any) => {
            if (gridItem.isEdited) {
                const operatorTechnicalPermissionToEdit = new OpOperatorTechnicalPermission(null)

                operatorTechnicalPermissionToEdit.endTime = gridItem.endTime
                operatorTechnicalPermissionToEdit.idOperator = operator.idOperator
                operatorTechnicalPermissionToEdit.idOperatorTechnicalPermission = gridItem.id
                operatorTechnicalPermissionToEdit.idTechnicalPermission = gridItem.idTechnicalPermission
                operatorTechnicalPermissionToEdit.notes = gridItem.notes
                operatorTechnicalPermissionToEdit.opState = Enums.OpChangeState.Modified
                operatorTechnicalPermissionToEdit.startTime = gridItem.startTime

                modifiedOperatorTechnicalPermissions.push(operatorTechnicalPermissionToEdit)
            }
        })

        _.forEach(grid, (gridItem: any) => {
            if (gridItem.isRemoved) {
                const operatorTechnicalPermissionToRemove = new OpOperatorTechnicalPermission(null)

                operatorTechnicalPermissionToRemove.endTime = gridItem.endTime
                operatorTechnicalPermissionToRemove.idOperator = operator.idOperator
                operatorTechnicalPermissionToRemove.idTechnicalPermission = gridItem.idTechnicalPermission
                operatorTechnicalPermissionToRemove.idOperatorTechnicalPermission = gridItem.id
                operatorTechnicalPermissionToRemove.notes = gridItem.notes
                operatorTechnicalPermissionToRemove.opState = Enums.OpChangeState.Delete
                operatorTechnicalPermissionToRemove.startTime = gridItem.startTime

                removedOperatorTechnicalPermissions.push(operatorTechnicalPermissionToRemove)
            }
        })

        return this.saveTechnicalPermissionsToOperator(operator, addedOperatorTechnicalPermissions, removedOperatorTechnicalPermissions, modifiedOperatorTechnicalPermissions)
    }

    //#endregion



    //#region Technical Permissions

    public getAvailableTechnicalPermissions(): JQueryPromise<OpTechnicalPermission[]> {
        return this.referenceTypeService.getDictionaryReferenceObjectList(Enums.ReferenceType.IdTechnicalPermission)
    }

    //#endregion
}

export class OperatorTechnicalPermissionsServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        OPERATOR_SERVICE_NAME,
        REFERENCE_TYPE_SERVICE_NAME,
        TECHNICAL_PERMISSION_SERVICE_NAME,
        function (
            operatorService: OperatorService,
            referenceTypeService: ReferenceTypeService,
            technicalPermissionsService: TechnicalPermissionService
        ) {
            return new OperatorTechnicalPermissionsService(operatorService, referenceTypeService, technicalPermissionsService)
        }]

    //#endregion
} 